import { EntityCreateResponse } from '@faro/http-clients';
import {
    CommandResponse,
    CycleGroup,
    RevisionedCommandResponse,
    StudyEntity,
    toCycleGroup,
} from '@faro/study-designer-model';
import axios from 'axios';
import { Identifiable } from 'faro-common-model';
import { EpochConfigurationQueryParameters } from '../EpochConfiguration/EpochConfigurationClient';
import { RevisionQueryParameter, StudyPathParameter } from '../parameter';

export type CycleGroupPathParameter = StudyPathParameter;

/**
 * Cycle groups belong to an epoch configuration, and a study has one per schema and one per
 * schedule of activities that has been unlinked from it - so listing is always scoped.
 */
export type CycleGroupQueryParameters = EpochConfigurationQueryParameters & RevisionQueryParameter;

/**
 * Client managing API for the {@link CycleGroup} entity.
 */
export const cycleGroupClient = {
    list: async (
        request?: CycleGroupPathParameter & CycleGroupQueryParameters,
        signal?: AbortSignal
    ): Promise<CycleGroup[]> => {
        const { studyId, ...query } = request ?? {};
        return axios
            .get(`/api/study-designer/studies/${studyId}/cycle-groups`, { signal, params: query })
            .then(response => response.data?.map(toCycleGroup) ?? []);
    },

    create: async (entity: CycleGroup, signal?: AbortSignal): Promise<EntityCreateResponse> => {
        return axios
            .post(`/api/study-designer/studies/${entity.studyId}/cycle-groups`, entity, { signal })
            .then(response => ({
                id: response.data.entityId,
                revision: response.data.revision,
            }));
    },

    update: async (entity: CycleGroup, signal?: AbortSignal): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .put(`/api/study-designer/studies/${entity.studyId}/cycle-groups/${entity.id}`, entity, { signal })
            .then(response => response.data);
    },

    delete: async (entity: StudyEntity, signal?: AbortSignal): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .delete(`/api/study-designer/studies/${entity.studyId}/cycle-groups/${entity.id}`, { signal })
            .then(response => response.data);
    },

    get: async (request: CycleGroupPathParameter & Identifiable, signal?: AbortSignal): Promise<CycleGroup> => {
        const { studyId, id } = request;
        return axios
            .get(`/api/study-designer/studies/${studyId}/cycle-groups/${id}`, { signal })
            .then(response => toCycleGroup(response.data));
    },

    map: toCycleGroup,
};
